import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import {
  PipelinePrResult,
  RunSummary,
} from '@internal/backstage-plugin-sql-pipeline-common';

import { sqlPipelineApiRef } from '../api/SqlPipelineApi';
import { PrCreatedDialog } from './PrCreatedDialog';
import { TicketField, useTicket } from './TicketField';

const RUN_WINDOW_DAYS = 7;

const fmtDate = (iso: string | null) => {
  if (!iso) return '기록 없음';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

/** 실행 이력 표시. 로딩·조회실패·정상 세 상태를 갖는다. */
const RunSummaryBody = (props: {
  loading: boolean;
  runs: RunSummary | null;
  active: boolean;
}) => {
  const { loading, runs, active } = props;

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={8}>
        <CircularProgress size={16} />
        <Typography variant="body2">실행 이력 확인 중...</Typography>
      </Box>
    );
  }

  if (runs?.unavailable) {
    return (
      <Typography variant="body2" color="textSecondary">
        {runs.unavailable} 실행 이력을 확인하지 못했으니 더 주의해주세요.
      </Typography>
    );
  }

  return (
    <Box display="flex" alignItems="flex-start" gridGap={8}>
      {active && <WarningIcon style={{ color: '#e65100', fontSize: 20 }} />}
      <Box>
        <Typography variant="body2">
          운영계 최근 {runs?.days ?? RUN_WINDOW_DAYS}일간{' '}
          <strong>{runs?.total ?? 0}회</strong> 실행
          {(runs?.failed ?? 0) > 0 && ` (실패 ${runs?.failed}회)`}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          마지막 실행: {fmtDate(runs?.lastRunAt ?? null)}
          {runs?.lastState ? ` — ${runs.lastState}` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * 파이프라인 삭제 다이얼로그.
 *
 * 되돌리기 어려운 작업이므로 체크박스 대신 **이름 타이핑**을 요구하고,
 * **최근 실행 이력**을 보여준다 — 활발히 도는 DAG 를 실수로 지우는 것을 막는다.
 * 이전 scaffolder 템플릿은 "삭제를 확인합니다" 체크박스 하나였다.
 */
export const DeletePipelineDialog = (props: {
  pipelineName: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { pipelineName, open, onClose } = props;
  const api = useApi(sqlPipelineApiRef);
  const alertApi = useApi(alertApiRef);

  const [typed, setTyped] = useState('');
  const ticketState = useTicket();
  const { ticket, reset: resetTicket } = ticketState;
  const [runs, setRuns] = useState<RunSummary | null>(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState<PipelinePrResult | null>(null);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      setRuns(
        await api.getRunSummary(pipelineName, {
          env: 'prod',
          days: RUN_WINDOW_DAYS,
        }),
      );
    } catch {
      // 조회 실패가 삭제를 막아서는 안 된다.
      setRuns(null);
    } finally {
      setLoadingRuns(false);
    }
  }, [api, pipelineName]);

  useEffect(() => {
    if (open) {
      setTyped('');
      resetTicket();
      loadRuns();
    }
  }, [open, loadRuns, resetTicket]);

  const nameMatches = typed === pipelineName;
  // 티켓 때문에 막지 않는다 — 누르면 티켓 칸이 빨갛게 이유를 알려준다.
  const canDelete = nameMatches && !deleting;

  const handleDelete = async () => {
    if (!ticketState.ensureValid()) return;
    setDeleting(true);
    try {
      const removed = await api.deletePipeline(pipelineName, {
        ticketNumber: ticket,
      });
      setResult(removed);
    } catch (e: any) {
      alertApi.post({
        message: e?.message ?? '삭제 PR 생성에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const active = (runs?.total ?? 0) > 0;

  if (result) {
    return (
      <PrCreatedDialog
        open={open}
        onClose={() => {
          setResult(null);
          onClose();
        }}
        title="삭제 PR 을 만들었습니다"
        result={result}
        outcome={
          result.promoteOnMerge
            ? '정상적으로 반영되면 [내 파이프라인]에서 사라집니다. 평균 3분정도 소요됩니다. ' +
              '운영계에도 있는 파이프라인이라, 머지 후 운영계 삭제 PR 이 자동으로 열립니다. ' +
              '그 PR 의 리뷰·머지까지 마쳐야 운영계에서 사라집니다.'
            : '정상적으로 반영되면 [내 파이프라인]에서 사라집니다. 평균 3분정도 소요됩니다.'
        }
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>파이프라인 삭제 — {pipelineName}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          <strong>{pipelineName}</strong> 의 DAG 파일과 카탈로그 항목을 삭제하는
          PR 을 생성합니다.
        </Typography>

        <Box
          my={2}
          p={1.5}
          borderRadius={4}
          style={{
            border: `1px solid ${active ? '#e65100' : '#666'}`,
            backgroundColor: active
              ? 'rgba(230, 81, 0, 0.1)'
              : 'rgba(255,255,255,0.04)',
          }}
        >
          <RunSummaryBody loading={loadingRuns} runs={runs} active={active} />
        </Box>

        <TicketField
          state={ticketState}
          helperText="PR 브랜치명으로 사용됩니다"
          style={{ marginBottom: 16 }}
        />

        <TextField
          label="확인을 위해 파이프라인 이름을 입력하세요"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          required
          fullWidth
          variant="outlined"
          size="small"
          placeholder={pipelineName}
          error={typed.length > 0 && !nameMatches}
          helperText={
            typed.length > 0 && !nameMatches
              ? '이름이 일치하지 않습니다'
              : `정확히 "${pipelineName}" 을 입력해주세요`
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          취소
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          disabled={!canDelete}
          style={
            canDelete ? { backgroundColor: '#c62828', color: '#fff' } : undefined
          }
          startIcon={
            deleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          삭제 PR 생성
        </Button>
      </DialogActions>
    </Dialog>
  );
};
